import { Avatar, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { GenericAbortSignal } from "axios";
import { MprcTypography } from "components";
import { DeployApplicationType, UserAnswersQuestionResponse, UserAnswersQuestionResponsePaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { useInstanceContext } from "views/assessment/player/AssPlayerBase";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    deployId: string;
    instanceId: string;
    questionId: string;
    alternativeId?: string;
    version: number;
    title: string;
    onClose: (item?: UserAnswersQuestionResponse) => void;
}

type ItemData = GenericListItemsType<UserAnswersQuestionResponse>;

export const UsersAnswerDrawer = ({ deployId, instanceId, questionId, version, alternativeId, title, onClose }: IProps) => {

    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const ctx = useInstanceContext();

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<UserAnswersQuestionResponsePaginatedList> => {

        if (alternativeId) {
            return new Promise((resolve, reject) => {
                api.v1.assPlayer.getUserAnswersAlternative(
                    {
                        deployId,
                        instanceId,
                        questionId,
                        version,
                        alternativeId,
                        onlyTrueAnswer: true,
                        ...genericFilter
                    },
                    { signal }
                )
                    .then((response) => resolve(response.data))
                    .catch(reject);
            });

        } else {
            return new Promise((resolve, reject) => {
                api.v1.assPlayer.getUserAnswersQuestion(
                    {
                        deployId,
                        instanceId,
                        questionId,
                        version,
                        ...genericFilter
                    },
                    { signal }
                )
                    .then((response) => resolve(response.data))
                    .catch(reject);
            });
        }

    };

    const renderRow = (props: ListChildComponentProps<ItemData>) => {
        const { index, style } = props;
        const { items, onViewerClick } = props.data;
        const item = items[index];

        return (
            <ListItem key={index} style={style} disableGutters onClick={() => onViewerClick && onViewerClick(item)} className={AppGlobalClassKeys.clickable}>
                <ListItemAvatar>
                    <Avatar
                        variant="circular"
                        src={item.thumbnailUrl!}
                        sx={(theme) => ({ width: 40, height: 40 })}
                    />
                </ListItemAvatar >
                <ListItemText
                    disableTypography
                    primary={<MprcTypography noWrapRows={1} variant="body2" fontWeight="bold" title={item.name}>{item.name}</MprcTypography>}
                    secondary={
                        item.totalAnswer > 1
                            ? <MprcTypography noWrapRows={1} variant="caption" color="muted.main">
                                {t("AssPlayer.UserDrawer.TimesText", { count: item.totalAnswer })}
                            </MprcTypography>
                            : undefined
                    }
                />
            </ListItem>
        );
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return <GenericListDrawer
        title={t(ctx.viewData?.deployApplicationType == DeployApplicationType.LmsManagerEfficacy ? "AssPlayer.UserDrawer.ManagerTitle" : "AssPlayer.UserDrawer.Title")}
        subtitle={title}
        itemHeight={48}
        onClose={onClose}
        onGetData={handleGetData}
        renderRow={renderRow}
    />;
};
